<template>
  <div class="article-person">
    <BHLink
      class="article-person__avatar"
      :href="person.url"
    >
      <img
        class="article-person__photo"
        :src="person.avatar"
        :alt="person.name"
      />
    </BHLink>
    <div class="article-person__info">
      <span class="article-person__info-elem article-person__name">
        <BHLink
          class="article-person__link"
          :href="person.url"
          colorless
        >
          {{ name }}
        </BHLink>
      </span>
      <div
        v-if="person.expertise"
        class="article-person__info-elem article-person__profession"
      >
        <TheContent
          v-for="itemChildren in expertise"
          :key="itemChildren.id"
          :elem="itemChildren"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import type { TAuthor, TContent } from '@/types/common';
  import BHLink from '~/components/BHLink.vue';
  import TheContent from '~/components/TheContent';

  export default defineNuxtComponent({
    name: 'ArticleCardPerson',
    components: {
      BHLink,
      TheContent,
    },

    props: {
      person: {
        type: Object as PropType<TAuthor>,
        required: true,
      },
    },

    data: () => ({
      nameLength: 28,
      expertiseLength: 30,
    }),

    computed: {
      name(): string {
        return this.person.name.length > this.nameLength
          ? this.person.name.slice(0, this.nameLength).replace(/\s+$/gm, '') + '...'
          : this.person.name;
      },

      expertise(): TContent[] {
        const expertise = this.person.expertise;
        const prepearedExpertise: TContent[] = [];
        let expertiseString = '';
        for (const item of expertise) {
          if (item.type === '#text') {
            const prepearedItem = this.prepareExpertiseTextNode(expertiseString, item);
            expertiseString += prepearedItem.text;
            prepearedExpertise.push(prepearedItem);
          }

          if (item.type === 'a' && item.children.length > 0) {
            const children = item.children[0];

            const newChildren = this.prepareExpertiseTextNode(expertiseString, children);
            const newItem = {
              ...item,
              children: [newChildren],
            };

            expertiseString += children.text;
            prepearedExpertise.push(newItem);
          }

          if (expertiseString.length > this.expertiseLength) {
            return prepearedExpertise;
          }
        }
        return prepearedExpertise;
      },
    },

    methods: {
      prepareExpertiseTextNode(expertiseString: string, item: TContent): TContent {
        const prevTextLength = expertiseString.length;
        const textLength = item.text.length;
        const absoluteLength = textLength + prevTextLength;

        if (absoluteLength > this.expertiseLength) {
          const diff = absoluteLength - this.expertiseLength;
          const newItem = {
            ...item,
          };
          newItem.text = item.text.slice(0, item.text.length - diff).replace(/\s+$/gm, '') + '...';
          return newItem;
        }

        return item;
      },

      prepareExpertise(content: TContent[]): TContent[] {
        return content;
      },
    },
  });
</script>

<style lang="scss" scoped>
  .article-person {
    $this: &;
    font-family: $text-font--1;
    display: flex;
    align-items: center;

    &--without-bio {
      align-items: center;
    }

    &__avatar {
      margin-right: $spacing-2;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      position: relative;
      flex-shrink: 0;

      &::before {
        content: '';
        transition: opacity 0.3s linear;
        position: absolute;
        width: calc(100% + 8px);
        height: calc(100% + 8px);
        top: -4px;
        left: -4px;
        border: 2px solid $hover-color--1;
        border-radius: 50%;
        opacity: 0;
      }

      @include hover {
        &::before {
          opacity: 1;
        }
      }
    }

    &__photo {
      display: block;
      width: 100%;
      height: 100%;
      border-radius: 50%;
    }

    &__info-elem {
      display: block;
    }

    &__name {
      word-break: break-word;
      @include smallText;
      @include hover {
        color: $hover-color--1;
      }

      @include tablet {
        @include normalText;
      }
    }

    &__profession {
      @include tinyText;
      color: $text-color--1;
      a {
        @include link-styles;
      }

      @include tablet {
        @include smallText;
      }
    }
  }
</style>
